'use client';

import { Analytics } from "@vercel/analytics/react";
import Link from "next/link";
import { createClient } from '@/utils/supabase/client';
import { useEffect, useState } from 'react';
import { signOutAction } from "@/app/actions";
import { usePathname, useSearchParams } from 'next/navigation';
import ProfilePic from "./ProfilePic";
import styles from './Header.module.css';
import LogoAndNameImage from "../Logo/LogoAndName";
import { User } from '@supabase/supabase-js';
import { getProfilePic } from '@/app/actions/profileActions';

// Create a singleton supabase client
const supabase = createClient();

function SignInLink() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const returnPath = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '')
  return (
    <Link 
      className={`
        ${styles.signInHeaderLP} ${styles.signInButton}`}
      href={`/sign-in?returnUrl=${encodeURIComponent(returnPath)}`}
    >
      Sign In
    </Link>
  )
}
export default function LandingPageHeader() {
  // Track three states: authenticated, unauthenticated, or loading
  const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    console.time('authCheck');
    let isSubscribed = true;
    
    try {
      const storedUser = localStorage.getItem('zilu.auth.user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (isSubscribed) {
          setUser(parsedUser);
          setAuthState('authenticated');
          
          // Also try to get profile pic from localStorage
          const storedPic = localStorage.getItem('zilu.auth.profilePic');
          if (storedPic) {
            setProfilePicUrl(storedPic);
          }
        }
      }
    } catch (e) {
      console.warn('Error reading stored auth state:', e);
    }
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isSubscribed) return;
      
      const newUser = session?.user || null;
      setUser(newUser);
      setAuthState(newUser ? 'authenticated' : 'unauthenticated');
      
      // Store in localStorage
      if (newUser) {
        localStorage.setItem('zilu.auth.user', JSON.stringify(newUser));
      } else {
        localStorage.removeItem('zilu.auth.user');
        localStorage.removeItem('zilu.auth.profilePic');
        setProfilePicUrl(null);
      }
    });
    
    // Get current auth state if not found in localStorage
    if (authState === 'loading') {
      supabase.auth.getUser().then(({ data }) => {
        if (!isSubscribed) return;
        
        if (data.user) {
          setUser(data.user);
          setAuthState('authenticated');
          localStorage.setItem('zilu.auth.user', JSON.stringify(data.user));
        } else {
          setAuthState('unauthenticated');
        }
      });
    }
    
    console.timeEnd('authCheck');
    
    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (authState !== 'authenticated' || !user) return;
    if (profilePicUrl) return;
    getProfilePic().then(({ profilePicUrl: newUrl }) => {
      if (newUrl) {
        setProfilePicUrl(newUrl);
        localStorage.setItem('zilu.auth.profilePic', newUrl);
      }
    });
  }, [authState, user, profilePicUrl]);

  return (
    <div className={styles.landingPageHeader}>
      <Analytics />
      <div className="mobile-logo mobile">
        <LogoAndNameImage className=" fixed" width={207} height={32} priority />
      </div>

      {authState === 'authenticated' && (
        <>
          <div className={styles.profileContainerLadningPage}>
            <ProfilePic profilePicUrl={profilePicUrl} width={32} height={32} />
          </div>
          <button className={styles.landingPageMenuButton}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            Menu
          </button>
        </>
      )}
      
      {authState === 'unauthenticated' && (
        <>
          <button className={`${styles.landingPageMenuButtonNoAuth} ${styles.landingPageMenuButton} ${styles.desktop}`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            Menu
          </button>
          <SignInLink />
        </>
      )}
      
      {authState === 'loading' && (
        // You could add a placeholder here if desired, or leave empty
        <div className={styles.authPlaceholder}></div>
      )}
   

      <button className="mobile-burger mobile mobile-burger-lp"
         onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 6.25C4 5.83579 4.33579 5.5 4.75 5.5H15.25C15.6642 5.5 16 5.83579 16 6.25C16 6.66421 15.6642 7 15.25 7H4.75C4.33579 7 4 6.66421 4 6.25ZM4 10C4 9.58578 4.33579 9.25 4.75 9.25H15.25C15.6642 9.25 16 9.58578 16 10C16 10.4142 15.6642 10.75 15.25 10.75H4.75C4.33579 10.75 4 10.4142 4 10ZM4 13.75C4 13.3358 4.33579 13 4.75 13H15.25C15.6642 13 16 13.3358 16 13.75C16 14.1642 15.6642 14.5 15.25 14.5H4.75C4.33579 14.5 4 14.1642 4 13.75Z" fill="#1E1C1C"/>
        </svg>
      </button>
      {/* Only show dropdown if authState is determined */}
      {authState !== 'loading' && isDropdownOpen && (
  <div className={`dropdown-menu ${authState === 'authenticated' ? 'dropdown-menu-landing-page-auth' : 'dropdown-menu-landing-page'}`}>
          <Link href="mailto:feedback@zundoku.com?subject=Feedback&body=Hi%20there%2C%0A%0AI'd%20like%20to%20provide%20some%20feedback%3A%0A%0A"
            className="dropdown-item">
            Give Feedback
          </Link>
          <Link href="/privacy-policy" className="dropdown-item">
            Privacy Policy
          </Link>
          <Link href="/terms-of-service" className="dropdown-item">
            Terms of Service
          </Link>
          <Link href="/impressum" className="dropdown-item">
          Impressum
          </Link>
          <Link href="/cookie-policy" className="dropdown-item">
            Cookie Policy
          </Link>
          {authState === 'authenticated' && (
            <button onClick={() => signOutAction()} className="dropdown-item">
             Sign Out
            </button>
          )}
        </div>
      )}
    </div> 
  );
}